import { LabelProps } from "@ptomasroos/react-native-multi-slider";
import React, { useCallback } from "react";

import BaseMultiSlider from "@/components/molecules/BaseMultiSlider";

/**
 *
 */
interface MultiSliderViewProps {
  value: number[];
  onChange: (value: [number, number]) => void;
  minValue: number;
  maxValue: number;
  step?: number;
  width?: number;
  renderCustomLabel?: React.ComponentType<LabelProps>;
  useLogScale?: boolean;
}

const POWER = 0.3;
const STEP_SIZE = 0.01;

/**
 * Module-level step size getter for consistent reference
 */
const getPowerStepSize = (): number => STEP_SIZE;

/**
 * Container component for power-scaled multi-slider with non-linear value transformation
 * @param props - Slider props excluding useLogScale
 * @param props.maxValue - Maximum value for the slider range
 * @param props.onChange - Callback fired when slider values change with transformed values
 */
const PowerSliderContainer: React.FC<
  Omit<MultiSliderViewProps, "useLogScale">
> = ({ maxValue, onChange, ...restProps }) => {
  const transformValue = useCallback((val: number): number => {
    return Math.pow(val, POWER);
  }, []);

  const inverseTransformValue = useCallback((val: number): number => {
    return Math.floor(Math.pow(val, 1 / POWER));
  }, []);

  const handleValuesChange = useCallback(
    (values: number[]) => {
      const actualValues = values.map(inverseTransformValue);
      const clampedValues = [
        actualValues[0],
        Math.min(Math.max(actualValues[1], actualValues[0]), maxValue),
      ] as [number, number];

      onChange(clampedValues);
    },
    [inverseTransformValue, maxValue, onChange]
  );

  return (
    <BaseMultiSlider
      {...restProps}
      maxValue={maxValue}
      onChange={onChange}
      transformValue={transformValue}
      inverseTransformValue={inverseTransformValue}
      getStepSize={getPowerStepSize}
      handleValuesChange={handleValuesChange}
    />
  );
};

export default PowerSliderContainer;
